import React, { useState, useEffect } from "react";
import CustomerLayout from "./CustomerLayout";
import { API_URL } from "../config";
import {
    Pencil,
    Trash2,
    Eye,
    Plus,
    Search,
    FileText,
    Loader2,
    X,
    AlertTriangle,
    RefreshCw,
    CheckCircle,
    Clock,
    Ban,
    Calendar,
    MapPin,
    User,
    Mail,
    Phone,
    Globe,
    CreditCard,
} from "lucide-react";

interface VisaApplication {
    id: number;
    reference_number: string;
    visa_pack_id: number;
    user_id: number | null;
    firstname: string;
    lastname: string;
    nationality: string;
    gender: string;
    passport_no: string;
    current_address: string;
    city: string;
    state: string | null;
    zipcode: string | null;
    purpose_of_visit: string;
    email: string;
    contact_no: string;
    status: "pending" | "under_review" | "embassy_processing" | "approved" | "rejected" | "cancelled";
    admin_notes: string | null;
    status_updated_at: string | null;
    is_guest: boolean;
    visa_number: string | null;
    visa_issue_date: string | null;
    visa_expiry_date: string | null;
    appointment_date: string | null;
    appointment_location: string | null;
    appointment_notes: string | null;
    created_at: string;
    updated_at: string;
    visa_pack: {
        id: number;
        name: string;
        destination_country: {
            id: number;
            name: string;
            code: string;
        };
    } | null;
}

interface FormattedVisa {
    id: number;
    reference: string;
    title: string;
    country: string;
    bookingDate: string;
    amount: string;
    status: "Approved" | "Pending" | "Rejected" | "Cancelled";
    createdDate: string;
    fullData: VisaApplication;
}

const MyRequestVisa: React.FC = () => {
    const [applications, setApplications] = useState<FormattedVisa[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [visaToDelete, setVisaToDelete] = useState<FormattedVisa | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const [visaToCancel, setVisaToCancel] = useState<FormattedVisa | null>(null);
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [cancelError, setCancelError] = useState<string | null>(null);

    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
    const [selectedVisa, setSelectedVisa] = useState<FormattedVisa | null>(null);
    const [detailsLoading, setDetailsLoading] = useState<boolean>(false);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const mapStatus = (status: string): FormattedVisa["status"] => {
        const map: Record<string, FormattedVisa["status"]> = {
            'pending': 'Pending',
            'under_review': 'Pending',
            'embassy_processing': 'Pending',
            'approved': 'Approved',
            'rejected': 'Rejected',
            'cancelled': 'Cancelled'
        };
        return map[status] || 'Pending';
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'approved': return { bg: '#e6f4ea', text: '#1e7e34', dot: '#1e7e34' };
            case 'pending': return { bg: '#fef3d6', text: '#b25900', dot: '#b25900' };
            case 'under_review': return { bg: '#e8f0fe', text: '#1a73e8', dot: '#1a73e8' };
            case 'embassy_processing': return { bg: '#e8f0fe', text: '#1a73e8', dot: '#1a73e8' };
            case 'rejected': return { bg: '#fce8e6', text: '#c5221f', dot: '#c5221f' };
            case 'cancelled': return { bg: '#f0ece4', text: '#6b7280', dot: '#6b7280' };
            default: return { bg: '#f0f0f0', text: '#666', dot: '#666' };
        }
    };

    const getStatusLabel = (status: string): string => {
        const map: Record<string, string> = {
            'pending': 'Pending',
            'under_review': 'Under Review',
            'embassy_processing': 'Embassy Processing',
            'approved': 'Approved',
            'rejected': 'Rejected',
            'cancelled': 'Cancelled'
        };
        return map[status] || status;
    };

    useEffect(() => {
        const fetchApplications = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(`${API_URL}/customer/my-visa-applications`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch your visa applications.");
                }

                const payload = await response.json();
                const rawData = payload.data || [];

                const formatted: FormattedVisa[] = rawData.map((item: VisaApplication) => ({
                    id: item.id,
                    reference: item.reference_number,
                    title: item.visa_pack?.name || "Visa Application",
                    country: item.visa_pack?.destination_country?.name || item.purpose_of_visit || "N/A",
                    bookingDate: item.appointment_date || formatDate(item.created_at),
                    amount: "Quote pending",
                    status: mapStatus(item.status),
                    createdDate: formatDate(item.created_at),
                    fullData: item,
                }));

                setApplications(formatted);
            } catch (err: any) {
                console.error("Fetch Visa Applications Error:", err);
                setError(err.message || "Unable to load visa applications.");
                setApplications([]);
            } finally {
                setLoading(false);
            }
        };

        fetchApplications();
    }, []);

    // Delete handlers
    const openDeleteModal = (visa: FormattedVisa) => {
        setVisaToDelete(visa);
        setDeleteError(null);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!visaToDelete) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const response = await fetch(`${API_URL}/customer/my-visa-applications/${visaToDelete.id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete application.");
            }

            setApplications((prev) => prev.filter((v) => v.id !== visaToDelete.id));
            setIsDeleteModalOpen(false);
            setVisaToDelete(null);
        } catch (err: any) {
            setDeleteError(err.message || "Deletion error.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Cancel handlers
    const openCancelModal = (visa: FormattedVisa) => {
        setVisaToCancel(visa);
        setCancelError(null);
        setIsCancelModalOpen(true);
    };

    const confirmCancel = async () => {
        if (!visaToCancel) return;

        setIsCancelling(true);
        setCancelError(null);

        try {
            const response = await fetch(`${API_URL}/customer/my-visa-applications/${visaToCancel.id}/cancel`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to cancel application.");
            }

            setApplications((prev) =>
                prev.map((v) => (v.id === visaToCancel.id ? { ...v, status: "Cancelled" as FormattedVisa["status"] } : v))
            );
            setIsCancelModalOpen(false);
            setVisaToCancel(null);
        } catch (err: any) {
            setCancelError(err.message || "Cancellation error.");
        } finally {
            setIsCancelling(false);
        }
    };

    // View details
    const handleViewDetails = async (visa: FormattedVisa) => {
        setSelectedVisa(visa);
        setIsDetailsModalOpen(true);
        setDetailsLoading(true);
        
        try {
            const response = await fetch(`${API_URL}/customer/my-visa-applications/${visa.id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (response.ok) {
                const payload = await response.json();
                const updatedData = payload.data;
                if (updatedData) {
                    setSelectedVisa({
                        ...visa,
                        fullData: updatedData,
                    });
                }
            }
        } catch (err) {
            console.error("Error fetching visa details:", err);
        } finally {
            setDetailsLoading(false);
        }
    };

    const canCancel = (status: string): boolean => {
        return ['pending', 'under_review'].includes(status);
    };

    const canDelete = (status: string): boolean => {
        return ['pending', 'cancelled', 'rejected'].includes(status);
    };

    // Stats
    const totalApplications = applications.length;
    const pendingCount = applications.filter(v => v.status === "Pending").length;
    const approvedCount = applications.filter(v => v.status === "Approved").length;
    const rejectedCount = applications.filter(v => v.status === "Rejected").length;

    const filteredApplications = applications.filter((visa) => {
        const matchesSearch =
            visa.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            visa.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
            visa.reference.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
    });

    return (
        <CustomerLayout>
            <style>{`
                .visa-container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 24px 40px;
                }

                .page-header-modern {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 32px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .page-header-modern h1 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 4px 0;
                }

                .page-header-modern p {
                    font-size: 15px;
                    color: #6b7280;
                    margin: 0;
                }

                .header-actions {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .btn-refresh {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-refresh:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .btn-primary-custom {
                    background-color: #294b5a;
                    color: #ffffff;
                    padding: 10px 24px;
                    border-radius: 12px;
                    border: none;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    transition: background 0.2s ease;
                    font-size: 13px;
                }

                .btn-primary-custom:hover {
                    background-color: #1a313d;
                }

                .visa-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 32px;
                }

                .visa-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .visa-stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                }

                .visa-stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .visa-stat-content h4 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                    line-height: 1.2;
                }

                .visa-stat-content p {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }

                .filters-modern {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 16px 20px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                }

                .search-modern {
                    position: relative;
                    flex: 1;
                    min-width: 240px;
                }

                .search-modern input {
                    width: 100%;
                    padding: 10px 14px 10px 40px;
                    border-radius: 10px;
                    border: 1px solid #e5e7eb;
                    background: #f9fafb;
                    font-size: 14px;
                    outline: none;
                    color: #1a1a2e;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }

                .search-modern input:focus {
                    border-color: #294b5a;
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(41, 75, 90, 0.08);
                }

                .search-modern svg {
                    position: absolute;
                    left: 13px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                }

                .visa-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }

                .visa-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr auto;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .visa-card:hover {
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border-color: #e5e7eb;
                }

                .visa-card-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .visa-icon-box {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .visa-info h4 {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin: 0 0 3px 0;
                }

                .visa-info .visa-ref {
                    font-size: 12px;
                    color: #6b7280;
                    font-family: monospace;
                }

                .visa-details-modern {
                    display: flex;
                    gap: 20px;
                    align-items: center;
                }

                .visa-meta-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .visa-meta-item .label {
                    font-size: 10px;
                    text-transform: uppercase;
                    color: #9ca3af;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }

                .visa-meta-item .value {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin-top: 2px;
                }

                .visa-status-modern {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 12px 4px 8px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .visa-actions {
                    display: flex;
                    gap: 4px;
                }

                .action-btn-modern {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    border: none;
                    background: transparent;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .action-btn-modern:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .action-btn-modern.delete:hover {
                    background: #fef2f2;
                    color: #dc2626;
                }

                .empty-state-modern {
                    text-align: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                }

                .empty-state-modern svg {
                    color: #d1d5db;
                    margin-bottom: 16px;
                }

                .empty-state-modern h3 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .empty-state-modern p {
                    color: #6b7280;
                    font-size: 14px;
                    margin: 0;
                }

                .loading-state-modern {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                    gap: 16px;
                }

                .spinner-modern {
                    animation: spin 0.8s linear infinite;
                    color: #294b5a;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .error-box-modern {
                    padding: 14px 18px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                    margin-bottom: 20px;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                .modal-content {
                    background: #ffffff;
                    border-radius: 20px;
                    max-width: 650px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                    padding: 32px;
                    position: relative;
                    animation: slideUp 0.25s ease;
                }

                .modal-content.delete-modal {
                    max-width: 440px;
                    text-align: center;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #edf2f4;
                    margin-bottom: 4px;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    color: #1a1a2e;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                }

                .modal-close-btn {
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 6px;
                    border-radius: 50%;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close-btn:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .modal-body {
                    margin-top: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .modal-section {
                    background: #f8fafc;
                    padding: 18px 20px;
                    border-radius: 12px;
                    border: 1px solid #eef2f6;
                }

                .modal-section-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    margin-bottom: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .details-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .detail-label {
                    font-size: 11px;
                    color: #9ca3af;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .delete-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: #fef2f2;
                    color: #dc2626;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px auto;
                }

                .delete-modal-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .delete-modal-desc {
                    font-size: 14px;
                    color: #6b7280;
                    margin: 0 0 20px 0;
                    line-height: 1.6;
                }

                .delete-flight-summary {
                    background: #f8fafc;
                    padding: 12px 16px;
                    border-radius: 12px;
                    margin-bottom: 24px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #eef2f6;
                }

                .delete-actions {
                    display: flex;
                    gap: 12px;
                }

                .btn-cancel {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    background: #ffffff;
                    color: #374151;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-cancel:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .btn-cancel:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .btn-delete-confirm {
                    flex: 1;
                    padding: 12px 16px;
                    border: none;
                    background: #dc2626;
                    color: #ffffff;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .btn-delete-confirm:hover {
                    background: #b91c1c;
                }

                .btn-delete-confirm:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .badge-status {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .badge-approved { background: #e6f4ea; color: #1e7e34; }
                .badge-pending { background: #fef3d6; color: #b25900; }
                .badge-rejected { background: #fce8e6; color: #c5221f; }
                .badge-cancelled { background: #f0ece4; color: #6b7280; }

                .error-box {
                    padding: 12px 16px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                }

                @media (max-width: 1024px) {
                    .visa-card {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }

                    .visa-details-modern {
                        flex-wrap: wrap;
                        gap: 12px;
                    }
                }

                @media (max-width: 768px) {
                    .visa-container {
                        padding: 0 16px 30px;
                    }

                    .page-header-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .header-actions {
                        flex-wrap: wrap;
                    }

                    .visa-stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .filters-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .search-modern {
                        min-width: unset;
                    }

                    .visa-card-left {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .visa-actions {
                        justify-content: flex-end;
                    }
                }

                @media (max-width: 480px) {
                    .visa-stats-grid {
                        grid-template-columns: 1fr 1fr;
                        gap: 10px;
                    }

                    .visa-stat-card {
                        padding: 14px 16px;
                    }

                    .visa-stat-content h4 {
                        font-size: 20px;
                    }
                }
            `}</style>

            <div className="visa-container">
                {/* Header */}
                <div className="page-header-modern">
                    <div>
                        <h1>My Visa Applications</h1>
                        <p>Manage and track the status of your visa requests</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={() => window.location.reload()}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && <div className="error-box-modern">{error}</div>}

                {/* Stats */}
                <div className="visa-stats-grid">
                    <div className="visa-stat-card">
                        <div className="visa-stat-icon" style={{ background: '#e8f0fe', color: '#1a73e8' }}>
                            <FileText size={24} />
                        </div>
                        <div className="visa-stat-content">
                            <h4>{totalApplications}</h4>
                            <p>Total Applications</p>
                        </div>
                    </div>

                    <div className="visa-stat-card">
                        <div className="visa-stat-icon" style={{ background: '#fef3d6', color: '#b25900' }}>
                            <Clock size={24} />
                        </div>
                        <div className="visa-stat-content">
                            <h4>{pendingCount}</h4>
                            <p>In Progress</p>
                        </div>
                    </div>

                    <div className="visa-stat-card">
                        <div className="visa-stat-icon" style={{ background: '#e6f4ea', color: '#1e7e34' }}>
                            <CheckCircle size={24} />
                        </div>
                        <div className="visa-stat-content">
                            <h4>{approvedCount}</h4>
                            <p>Approved</p>
                        </div>
                    </div>

                    <div className="visa-stat-card">
                        <div className="visa-stat-icon" style={{ background: '#fce8e6', color: '#c5221f' }}>
                            <Ban size={24} />
                        </div>
                        <div className="visa-stat-content">
                            <h4>{rejectedCount}</h4>
                            <p>Rejected / Cancelled</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-modern">
                    <div className="search-modern">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search by visa type, country or reference..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Visa Cards */}
                {loading ? (
                    <div className="loading-state-modern">
                        <Loader2 className="spinner-modern" size={36} />
                        <span style={{ color: '#6b7280' }}>Loading your visa applications...</span>
                    </div>
                ) : filteredApplications.length > 0 ? (
                    <div className="visa-grid">
                        {filteredApplications.map((visa) => {
                            const statusColor = getStatusColor(visa.status);
                            return (
                                <div key={visa.id} className="visa-card">
                                    <div className="visa-card-left">
                                        <div className="visa-icon-box">
                                            <Globe size={20} color="#294b5a" />
                                        </div>
                                        <div className="visa-info">
                                            <h4>{visa.title}</h4>
                                            <span className="visa-ref">Ref: {visa.reference}</span>
                                        </div>
                                    </div>

                                    <div className="visa-details-modern">
                                        <div className="visa-meta-item">
                                            <span className="label">Country</span>
                                            <span className="value">{visa.country}</span>
                                        </div>
                                        <div className="visa-meta-item">
                                            <span className="label">Submitted</span>
                                            <span className="value">{visa.createdDate}</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div 
                                            className="visa-status-modern"
                                            style={{ background: statusColor.bg, color: statusColor.text }}
                                        >
                                            <span className="status-dot" style={{ background: statusColor.dot }} />
                                            {visa.status}
                                        </div>

                                        <div className="visa-actions">
                                            <button 
                                                className="action-btn-modern" 
                                                title="View Details"
                                                onClick={() => handleViewDetails(visa)}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            
                                            {canCancel(visa.fullData?.status || '') && (
                                                <button
                                                    className="action-btn-modern"
                                                    title="Cancel application"
                                                    onClick={() => openCancelModal(visa)}
                                                >
                                                    <Ban size={16} />
                                                </button>
                                            )}

                                            {canDelete(visa.fullData?.status || '') && (
                                                <button
                                                    className="action-btn-modern delete"
                                                    title="Delete"
                                                    onClick={() => openDeleteModal(visa)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-state-modern">
                        <FileText size={48} />
                        <h3>No visa applications found</h3>
                        <p>Try adjusting your search criteria</p>
                    </div>
                )}
            </div>

            {/* Delete Modal */}
            {isDeleteModalOpen && visaToDelete && (
                <div className="modal-overlay" onClick={() => !isDeleting && setIsDeleteModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Delete Application?</h3>
                        <p className="delete-modal-desc">
                            Are you sure you want to permanently delete this visa application? This action cannot be undone.
                        </p>

                        <div className="delete-flight-summary">
                            <span>{visaToDelete.title} ({visaToDelete.reference})</span>
                        </div>

                        {deleteError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {deleteError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Deleting...
                                    </>
                                ) : (
                                    "Delete Application"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Modal */}
            {isCancelModalOpen && visaToCancel && (
                <div className="modal-overlay" onClick={() => !isCancelling && setIsCancelModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Cancel this application?</h3>
                        <p className="delete-modal-desc">
                            This will cancel your visa application. You can always submit a new request afterwards.
                        </p>

                        <div className="delete-flight-summary">
                            <span>{visaToCancel.title} ({visaToCancel.reference})</span>
                        </div>

                        {cancelError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {cancelError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setIsCancelModalOpen(false)}
                                disabled={isCancelling}
                            >
                                Keep Application
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmCancel}
                                disabled={isCancelling}
                            >
                                {isCancelling ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Cancelling...
                                    </>
                                ) : (
                                    "Cancel Application"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {isDetailsModalOpen && selectedVisa && (
                <div className="modal-overlay" onClick={() => setIsDetailsModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><FileText size={20} /> Visa Application Details</h3>
                            <button className="modal-close-btn" onClick={() => setIsDetailsModalOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        {detailsLoading ? (
                            <div className="loading-state-modern">
                                <Loader2 className="spinner-modern" size={32} />
                                <span>Loading details...</span>
                            </div>
                        ) : (
                            <div className="modal-body">
                                <div className="modal-section">
                                    <div className="modal-section-title"><FileText size={14} /> Application Info</div>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <span className="detail-label">Reference</span>
                                            <span className="detail-value">{selectedVisa.reference}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Status</span>
                                            <span className={`badge-status badge-${selectedVisa.status.toLowerCase()}`}>
                                                {selectedVisa.status}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Visa Type</span>
                                            <span className="detail-value">{selectedVisa.title}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Country</span>
                                            <span className="detail-value">{selectedVisa.country}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Submitted</span>
                                            <span className="detail-value">{selectedVisa.createdDate}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Purpose</span>
                                            <span className="detail-value">{selectedVisa.fullData?.purpose_of_visit || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-section">
                                    <div className="modal-section-title"><User size={14} /> Personal Information</div>
                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <span className="detail-label">Full Name</span>
                                            <span className="detail-value">{selectedVisa.fullData?.firstname} {selectedVisa.fullData?.lastname}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Nationality</span>
                                            <span className="detail-value">{selectedVisa.fullData?.nationality || "N/A"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Passport</span>
                                            <span className="detail-value">{selectedVisa.fullData?.passport_no || "N/A"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Email</span>
                                            <span className="detail-value">{selectedVisa.fullData?.email || "N/A"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Phone</span>
                                            <span className="detail-value">{selectedVisa.fullData?.contact_no || "N/A"}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="detail-label">Address</span>
                                            <span className="detail-value">{selectedVisa.fullData?.current_address || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>

                                {selectedVisa.fullData?.admin_notes && (
                                    <div className="modal-section">
                                        <div className="modal-section-title"><FileText size={14} /> Admin Notes</div>
                                        <p style={{ fontSize: '14px', color: '#1a1a2e', margin: 0 }}>
                                            {selectedVisa.fullData.admin_notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </CustomerLayout>
    );
};

export default MyRequestVisa;